'use client';

import type { DayCellContentArg, EventContentArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import { Stack, Tooltip, Typography } from '@mui/material';
import type { SnapshotStatus } from '@repo/api-gw-sdk';
import { useMemo, useRef } from 'react';

import { DATE_FORMAT, dayjs, type Dayjs } from '@/utils/dayjs';

import { PITCalendarWrapper } from './PITCalendarWrapper';
import { SnapshotIndicator } from './SnapshotIndicator';
import { OtherSnapshotsMultipleDots } from './otherSnapshotsMultipleDots';
import { PitrSnapshotsMultipleDots } from './pitrSnapshotsMultipleDots';
import { ShieldIcon } from './shieldIcon';

import { SkullIcon } from '../security/skullIcon';
import { Icon } from '../shared/icon';

export interface PointInTimeEvent {
  indicatorColor: string | null;
  vaultName?: string;
  start: Date;
  status?: SnapshotStatus;
  accessDeniedItemsCount?: number;
  isProviderSnapshot?: boolean;
  hasSecurityIssue?: boolean;
  supportPiTR?: boolean;
  protectedFromDeletion?: boolean;
  onHold?: boolean;
}

interface InternalPointInTimeEvent extends PointInTimeEvent {
  additionalEventsInDate?: number;
}

interface PointsInTimeCalendarProps {
  isLoading?: boolean;
  initialDate: Date;
  events: PointInTimeEvent[];
  selectedDate: Dayjs | undefined;
  onDateSelected: (date: Dayjs | undefined) => void;
  onDateRangeChanged: (start: Dayjs, end: Dayjs) => void;
}

export const PointsInTimeCalendar = (props: PointsInTimeCalendarProps) => {
  const calendarRef = useRef<FullCalendar>(null);

  const {
    summarizedEvents,
    securityIssuesMapper,
    protectedSnapshotsMapper,
    holdSnapshotsMapper,
  } = useMemo(() => {
    const map = new Map<string, InternalPointInTimeEvent[]>();
    const sorted = [...props.events].sort((a, b) =>
      dayjs.utc(a.start).isAfter(dayjs.utc(b.start)) ? 1 : -1
    );

    const supportPiTRDates = new Set<string>();

    sorted.forEach((event) => {
      const date = dayjs.utc(event.start).format(DATE_FORMAT);

      if (event.supportPiTR && supportPiTRDates.has(date)) {
        return;
      }

      const events = map.get(date) || [];
      if (events.length === 2) {
        events[1].additionalEventsInDate =
          (events[1].additionalEventsInDate || 0) + 1;
      } else {
        events.push(event);
        if (event.supportPiTR) {
          supportPiTRDates.add(date);
        }
      }
      map.set(date, events);
    });

    // setting end event to be on the same day as the start one
    const summarizedEvents = [...map.values()].flat().map((event) => {
      const start = dayjs.utc(event.start);
      const end = start.endOf('day');

      return {
        ...event,
        start: start.toDate(),
        end: end.toDate(),
      };
    });

    const securityGroupedByDay = props.events.reduce((acc, event) => {
      const date = dayjs.utc(event.start).format(DATE_FORMAT);
      if (event.hasSecurityIssue) {
        acc.set(date, true);
      }
      return acc;
    }, new Map<string, boolean>());

    const protectedGroupedByDay = props.events.reduce((acc, event) => {
      const date = dayjs.utc(event.start).format(DATE_FORMAT);
      if (event.protectedFromDeletion) {
        acc.set(date, true);
      }
      return acc;
    }, new Map<string, boolean>());

    const holdGroupedByDay = props.events.reduce((acc, event) => {
      const date = dayjs.utc(event.start).format(DATE_FORMAT);
      if (event.onHold) {
        acc.set(date, true);
      }
      return acc;
    }, new Map<string, boolean>());

    return {
      summarizedEvents,
      securityIssuesMapper: Object.fromEntries(securityGroupedByDay),
      protectedSnapshotsMapper: Object.fromEntries(protectedGroupedByDay),
      holdSnapshotsMapper: Object.fromEntries(holdGroupedByDay),
    };
  }, [props.events]);

  return (
    <PITCalendarWrapper>
      <FullCalendar
        ref={calendarRef}
        timeZone='UTC'
        initialDate={props.initialDate}
        plugins={[dayGridPlugin, interactionPlugin]}
        initialView='dayGridMonth'
        weekends={true}
        fixedWeekCount={false}
        showNonCurrentDates={false}
        buttonText={{ today: 'Today' }}
        dayCellContent={(arg: DayCellContentArg) =>
          renderDayCell(
            arg,
            securityIssuesMapper,
            protectedSnapshotsMapper,
            holdSnapshotsMapper
          )
        }
        dayCellClassNames={(args) => {
          const classes: string[] = [];
          if (props.isLoading) {
            classes.push('cell-loading');
          }

          const date = dayjs.utc(args.date);
          if (
            props.selectedDate &&
            dayjs.utc(props.selectedDate).isSame(date, 'date')
          ) {
            classes.push('cell-selected');
          }

          if (securityIssuesMapper?.[date.format(DATE_FORMAT)]) {
            classes.push('cell-error');
          }

          if (
            summarizedEvents.every(
              (x) => !dayjs.utc(x.start).isSame(date, 'date')
            )
          ) {
            classes.push('fc-day-disabled');
          }

          return classes;
        }}
        eventClick={(args) => {
          if (args.event.start) {
            props.onDateSelected(dayjs.utc(args.event.start));
          }
        }}
        dateClick={(args) => {
          const date = dayjs.utc(args.date);
          if (
            summarizedEvents.some((x) =>
              dayjs.utc(x.start).isSame(date, 'date')
            )
          ) {
            props.onDateSelected(dayjs.utc(args.date));
          }
        }}
        selectable={true}
        dayHeaders={false}
        events={summarizedEvents}
        eventContent={renderEventContent}
        customButtons={{
          myPrev: {
            icon: 'chevron-left',
            click: () => {
              calendarRef.current?.getApi().prev();
              props.onDateRangeChanged(
                dayjs
                  .utc(calendarRef.current?.getApi().getDate())
                  .startOf('month'),
                dayjs
                  .utc(calendarRef.current?.getApi().getDate())
                  .endOf('month')
              );
            },
          },
          myNext: {
            icon: 'chevron-right',
            click: () => {
              calendarRef.current?.getApi().next();
              props.onDateRangeChanged(
                dayjs
                  .utc(calendarRef.current?.getApi().getDate())
                  .startOf('month'),
                dayjs
                  .utc(calendarRef.current?.getApi().getDate())
                  .endOf('month')
              );
            },
          },
          today: {
            text: 'Today',
            click: () => {
              calendarRef.current?.getApi().today();
              props.onDateRangeChanged(
                dayjs
                  .utc(calendarRef.current?.getApi().getDate())
                  .startOf('month'),
                dayjs
                  .utc(calendarRef.current?.getApi().getDate())
                  .endOf('month')
              );
            },
          },
        }}
        headerToolbar={{
          left: 'title',
          right: 'today,myPrev,myNext',
        }}
        height='auto'
      />
    </PITCalendarWrapper>
  );
};

const renderEventContent = (args: EventContentArg) => {
  return (
    <Stack direction='row' alignItems='center' data-testid='snapshot-dot'>
      {args.event._def.extendedProps.supportPiTR ? (
        <PitrSnapshotsMultipleDots
          fill={args.event._def.extendedProps.indicatorColor}
        />
      ) : args.event._def.extendedProps.isProviderSnapshot ? (
        <OtherSnapshotsMultipleDots />
      ) : (
        <SnapshotIndicator
          data-testid='snapshot-indicator'
          isPartial={!!args.event._def.extendedProps.accessDeniedItemsCount}
          backgroundColor={args.event._def.extendedProps.indicatorColor || null}
        />
      )}
      {args.event._def.extendedProps.additionalEventsInDate &&
        args.event._def.extendedProps.additionalEventsInDate > 0 && (
          <span data-testid='snapshot-more-indicator' className='ml-[4px]'>
            +{args.event._def.extendedProps.additionalEventsInDate}
          </span>
        )}
    </Stack>
  );
};

function renderDayCell(
  arg: DayCellContentArg,
  securityIssuesMapper: Record<string, boolean> | undefined,
  protectedSnapshotsMapper: Record<string, boolean> | undefined,
  holdSnapshotsMapper: Record<string, boolean> | undefined
) {
  const dateKey = dayjs.utc(arg.date).format(DATE_FORMAT);
  const hasSecurityIssue = securityIssuesMapper?.[dateKey];
  const hasProtectedSnapshot = protectedSnapshotsMapper?.[dateKey];
  const hasHoldSnapshot = holdSnapshotsMapper?.[dateKey];
  return (
    <div>
      <Stack
        className='absolute left-1/2 -translate-x-1/2 bg-(--mui-palette-background-paper)'
        marginTop='-36px'
        direction='row'
        alignItems='center'
      >
        {hasHoldSnapshot ? (
          <Icon iconClass='material-symbols-all-inclusive mt-[-2px]' />
        ) : (
          hasProtectedSnapshot && (
            <Tooltip
              title="The last clean snapshot won't be cleaned by the retention scheduler"
              placement='top'
              arrow
            >
              <ShieldIcon />
            </Tooltip>
          )
        )}
        {hasSecurityIssue && <SkullIcon />}
      </Stack>
      <Typography>
        {dayjs.utc(arg.date).format('ddd')} {arg.dayNumberText}
      </Typography>
    </div>
  );
}
